import type { AutomobileBodyType, AutomobileModel } from '../types/automobile';
import { AUTOMOBILE_BODY_TYPE_ORDER } from '../types/automobile';

const COMMERCIAL_BODIES = new Set<AutomobileBodyType>([
  'kamyon',
  'tir',
  'otobus',
  'kamyonet',
  'pickup',
  'minibus',
  'traktor',
  'motosiklet',
]);

const BODY_PRIORITY = new Map(
  AUTOMOBILE_BODY_TYPE_ORDER.map((body, index) => [body, AUTOMOBILE_BODY_TYPE_ORDER.length - index]),
);

function bodyPriority(body: AutomobileBodyType): number {
  return BODY_PRIORITY.get(body) ?? 0;
}

function modelRichness(model: AutomobileModel): number {
  let score = 0;
  if (model.generationCode) score += 8;
  if (model.variantLabel) score += 4;
  if (model.yearFrom) score += 2;
  if (model.yearFromMonth) score += 1;
  if (model.imageUrl) score += 3;
  return score;
}

export function automobileModelVariantGroupKey(model: AutomobileModel): string {
  return [
    (model.name || '').toLowerCase(),
    model.bodyType || '',
    (model.generationCode || '').toLowerCase(),
    (model.variantLabel || '').toLowerCase(),
    model.yearFrom ?? '',
    model.yearFromMonth ?? '',
    model.yearTo ?? '',
  ].join('|');
}

function narrowPool(models: AutomobileModel[]): AutomobileModel[] {
  const bodies = new Set(models.map((m) => m.bodyType));
  const has = (b: AutomobileBodyType) => bodies.has(b);
  let pool = models;

  if (has('kamyonet') && (has('sedan') || has('minivan') || has('suv'))) {
    pool = pool.filter((m) => m.bodyType === 'kamyonet');
  } else if (has('pickup') && (has('sedan') || has('suv') || has('hatchback'))) {
    pool = pool.filter((m) => m.bodyType === 'pickup');
  } else if (has('tir') && has('kamyon')) {
    pool = pool.filter((m) => m.bodyType === 'tir');
  } else if (has('otobus') && has('kamyon')) {
    pool = pool.filter((m) => m.bodyType === 'otobus');
  }

  const commercial = pool.filter((m) => COMMERCIAL_BODIES.has(m.bodyType));
  const passenger = pool.filter((m) => !COMMERCIAL_BODIES.has(m.bodyType));
  if (commercial.length && passenger.length) return commercial;
  return pool;
}

export function pickCanonicalAutomobileModel(models: AutomobileModel[]): AutomobileModel {
  if (models.length === 1) return models[0];
  const pool = narrowPool(models);
  return [...pool].sort((a, b) => {
    const dr = modelRichness(b) - modelRichness(a);
    if (dr !== 0) return dr;
    return bodyPriority(b.bodyType) - bodyPriority(a.bodyType);
  })[0];
}

/** Aynı ad/nesil/gövde için tek kart — farklı yıl aralıkları ayrı kalır */
export function dedupeAutomobileModelsByVariant(brandSlug: string, models: AutomobileModel[]): AutomobileModel[] {
  void brandSlug;
  const byVariant = new Map<string, AutomobileModel[]>();
  for (const m of models) {
    const vk = automobileModelVariantGroupKey(m);
    const bucket = byVariant.get(vk) || [];
    bucket.push(m);
    byVariant.set(vk, bucket);
  }
  return [...byVariant.values()].map((bucket) => pickCanonicalAutomobileModel(bucket));
}
